import {
  LayoutAuth, LayoutDefault, LayoutReports, LayoutChildren
} from '@/components/layouts';
import store from '@/store';
import { isPortalIncidentsUser } from '@/util/cad-incident-utils'
import { resolveLandingRoute, firstAccessibleChild } from './access'

// Destino del redirect de una sección: el primer hijo que este usuario sí puede
// abrir. Lee los hijos REALES de `protectedRoute` en vez de una copia a mano
// (que en `/administracion` ya se había desincronizado: omitía
// `/users-by-municipio`). Se evalúa al navegar, no al cargar el módulo, así que
// `protectedRoute` ya existe cuando esto corre.
const primerHijoAccesible = (sectionPath, fallback = '/map') => {
  // El árbol se lee al navegar, no al cargar el módulo: para entonces
  // `protectedRoute` lleva rato definido.
  // eslint-disable-next-line no-use-before-define
  const hit = firstAccessibleChild(protectedRoute, sectionPath, store.getters['auth/getUser']);
  return hit ? hit.path : fallback;
};

export const publicRoute = [
  {
    path: '/',
    redirect: () => resolveLandingRoute(store.getters['auth/getUser']),
  },
  {
    path: '*',
    component: () => import('@/views/error/NotFound.vue'),
  },
  {
    path: '/login',
    redirect: '/auth/login',
  },
  // /reports/devices se retiró el 2026-10-14 (ver el comentario en el lugar
  // que ocupaba, dentro de /reports). La URL sigue respondiendo porque hay
  // marcadores y pestañas abiertas que apuntan a ella, y el deep-link
  // ?deviceId/?period lo abría en pestaña nueva el drawer del mapa antes de
  // que llevara directo a historial. Vive aquí y no entre los hijos de
  // /reports para que no cuente como entrada de menú ni como hijo accesible
  // de la sección. Sin nombre a propósito: nadie debe volver a navegar a ella.
  {
    path: '/reports/devices',
    redirect: (to) => (to.query.deviceId || to.query.period
      ? { name: 'historial-unidades-reports', query: to.query }
      : { name: 'unidades-reports' }),
  },
  {
    path: '/auth',
    component: LayoutAuth,
    meta: {
      title: 'Login',
    },
    redirect: '/auth/login',
    hidden: true,
    children: [
      {
        path: 'login',
        name: 'login',
        meta: {
          title: 'Login',
        },
        component: () => import('@/views/Login.vue'),
      },
      {
        path: 'logout',
        name: 'logout',
        component: {
          beforeRouteEnter() {
            store.dispatch('auth/logout', null, { root: true });
          },
        },
      },
    ],
  },

  {
    path: '/404',
    name: '404',
    meta: {
      title: 'Not Found',
    },
    component: () => import('@/views/error/NotFound.vue'),
  },

  {
    path: '/500',
    name: '500',
    meta: {
      title: 'Server Error',
    },
    component: () => import('@/views/error/Error.vue'),
  },
  {
    path: '/murphy-find',
    name: 'murphy-find',
    component: () => import('@/views/MurphyFindView.vue'),
    meta: {
      title: 'murphy',
      // Acepta booleano y cadena: los flags de `attributes` llegan en ambos
      // formatos según el path de escritura (el checkbox del formulario
      // escribe booleano; el editor libre de atributos, cadena). Comparar solo
      // contra `'true'` hacía que otorgarlo por checkbox no surtiera efecto.
      // Mismo criterio que `isPortalIncidentsUser`.
      validate: (user) => {
        const flag = user.attributes.murphyfind;
        return flag === true || flag === 'true';
      },
    },
  },
];

export const protectedRoute = [
  {
    path: '/admin',
    name: 'admin',
    component: LayoutDefault,
    meta: {
      title: 'home',
      group: 'apps',
      icon: '',
    },
    children: [
      {
        path: '/home',
        name: 'home',
        meta: {
          title: 'home',
          group: 'apps',
          icon: '$home',
          admin: true,
        },
        component: () => import('@/views/Home.vue'),
      },
      {
        path: '/administracion',
        name: 'administracion',
        component: LayoutChildren,
        redirect: () => primerHijoAccesible('/administracion'),
        meta: {
          title: 'administracion',
          group: 'apps',
          icon: 'mdi-account-cog',
        },
        children: [
          {
            path: '/users',
            name: 'users',
            meta: {
              title: 'users',
              group: 'apps',
              icon: '$user',
              admin: true,
            },
            component: () => import('@/views/Users.vue'),
          },
          {
            path: '/users-by-municipio',
            name: 'users-by-municipio',
            meta: {
              title: 'users-by-municipio',
              group: 'apps',
              icon: 'mdi-city',
              // Reporte de consulta para C5: cuántos usuarios tiene cada
              // municipio integrado a Kalan. Acceso para admins y para cuentas
              // con el flag permission_user_stats (usuario de consulta).
              validate: (user) => user.administrator === true
                || (user.attributes && user.attributes.permission_user_stats === true),
            },
            component: () => import('@/views/UsersByMunicipio.vue'),
          },
          {
            path: '/profiles',
            name: 'profiles',
            meta: {
              title: 'profiles',
              group: 'apps',
              icon: '$group',
              admin: true,
            },
            component: () => import('@/views/Profiles.vue'),
          },
          {
            path: '/groups',
            name: 'groups',
            meta: {
              title: 'groups',
              group: 'apps',
              icon: '$group',
              validate: (user) => {
                return user.attributes.permission_groups;
              },
            },
            component: () => import('@/views/Groups.vue'),
          },
        ],
      },
      {
        path: '/video',
        name: 'video',
        component: LayoutChildren,
        redirect: '/cameras',
        meta: {
          title: 'video',
          group: 'apps',
          icon: 'mdi-cctv',
        },
        children: [
          {
            path: '/cameras',
            name: 'cameras',
            meta: {
              title: 'cameras',
              group: 'apps',
              icon: '$camera',
              validate: (user) => {
                return user.attributes.permission_cameras;
              },
            },
            component: () => import('@/views/Cameras.vue'),
          },
          {
            path: '/video-servers',
            name: 'video-servers',
            meta: {
              title: 'video-servers',
              group: 'apps',
              icon: 'mdi-server',
              iconColor: 'black',
              validate: (user) => !!user.attributes.permission_cameras,
            },
            component: () => import('@/views/VideoServers.vue'),
          },
        ],
      },
      {
        path: '/bus',
        name: 'bus',
        meta: {
          title: 'bus',
          group: 'apps',
          icon: '$bus',
          validate: (user) => {
            return user.attributes.permission_bus;
          },
        },
        component: () => import('@/views/Bus.vue'),
      },
      {
        path: '/external-devices',
        name: 'external-devices',
        meta: {
          title: 'external-devices',
          group: 'apps',
          icon: '$bus',
          validate: (user) => {
            return user.attributes.permission_bus;
          },
        },
        component: () => import('@/views/ExternalDevices.vue'),
      },
      {
        path: '/geofences',
        name: 'geofences',
        meta: {
          title: 'geofences',
          group: 'apps',
          icon: '$geofence',
          validate: (user) => {
            return user.attributes.permission_geofences;
          },
        },
        component: () => import('@/views/Geofences.vue'),
      },
      {
        path: '/mando-unico',
        name: 'mando-unico',
        component: LayoutChildren,
        meta: {
          title: 'mando-unico',
          group: 'apps',
          icon: '$geofence',
          validate: (user) => user.attributes?.permission_mando_unico,
        },
        children: [
          {
            path: '/mando-unico/geofences',
            name: 'mando-unico-geofences',
            meta: {
              title: 'mando-unico-geofences',
              group: 'apps',
              icon: '$geofence',
            },
            component: () => import('@/views/MandoUnicoGeofences.vue'),
          },
          {
            path: '/mando-unico/region',
            name: 'mando-unico-region',
            meta: {
              title: 'mando-unico-region',
              group: 'apps',
              icon: '$geofence',
            },
            component: () => import('@/views/MandoUnicoRegion.vue'),
          },
          {
            path: '/mando-unico/sector',
            name: 'mando-unico-sector',
            meta: {
              title: 'mando-unico-sector',
              group: 'apps',
              icon: '$geofence',
            },
            component: () => import('@/views/MandoUnicoSector.vue'),
          },
          {
            path: '/mando-unico/cuadrante',
            name: 'mando-unico-cuadrante',
            meta: {
              title: 'mando-unico-cuadrante',
              group: 'apps',
              icon: '$geofence',
            },
            component: () => import('@/views/MandoUnicoCuadrante.vue'),
          },
        ],
      },
      // {
      //   path: '/incidents',
      //   name: 'incidents',
      //   meta: {
      //     title: 'incidents',
      //     group: 'apps',
      //     icon: 'mdi-alert',
      //   },
      //   component: () => import('@/views/Incidents.vue'),
      // },
      {
        path: '/analiticos',
        name: 'analiticos',
        component: LayoutChildren,
        redirect: '/lpr',
        meta: {
          title: 'analiticos',
          group: 'apps',
          icon: '$lpr',
        },
        children: [
          {
            path: '/lpr',
            name: 'lpr',
            meta: {
              title: 'lpr',
              group: 'apps',
              icon: '$lpr',
              validate: (user) => {
                return user.attributes.permission_lpr;
              },
            },
            component: () => import('@/views/LPR.vue'),
          },
          {
            path: '/lpr-categories',
            name: 'lpr-categories',
            meta: {
              title: 'lpr-categories',
              group: 'apps',
              icon: 'mdi-tag-multiple',
              validate: (user) => {
                return user.attributes.permission_lpr;
              },
            },
            component: () => import('@/views/LPRCategories.vue'),
          },
        ],
      },
      {
        path: '/listas-negras',
        name: 'listas-negras',
        component: LayoutChildren,
        redirect: '/lista-negra',
        meta: {
          title: 'listas-negras',
          group: 'apps',
          icon: 'mdi-car-off',
          validate: (user) => user.attributes.permission_blacklist,
        },
        children: [
          {
            path: '/lista-negra',
            name: 'lista-negra',
            meta: {
              title: 'lista-negra',
              group: 'apps',
              icon: 'mdi-format-list-bulleted',
              validate: (user) => user.attributes.permission_blacklist,
            },
            component: () => import('@/views/BlacklistEntries.vue'),
          },
          {
            path: '/lista-negra-categorias',
            name: 'lista-negra-categorias',
            meta: {
              title: 'lista-negra-categorias',
              group: 'apps',
              icon: 'mdi-tag-multiple',
              validate: (user) => user.attributes.permission_blacklist,
            },
            component: () => import('@/views/BlacklistCategories.vue'),
          },
          {
            path: '/lista-negra-webhooks',
            name: 'lista-negra-webhooks',
            meta: {
              title: 'lista-negra-webhooks',
              group: 'apps',
              icon: 'mdi-webhook',
              validate: (user) => user.attributes.permission_blacklist,
            },
            component: () => import('@/views/BlacklistWebhookSubscribers.vue'),
          },
        ],
      },
      {
        path: '/botones-panico',
        name: 'botones',
        component: LayoutChildren,
        redirect: '/sensor',
        meta: {
          title: 'botones',
          group: 'apps',
          icon: '$sensor',
        },
        children: [
          {
            path: '/sensor',
            name: 'sensor',
            meta: {
              title: 'sensor',
              group: 'apps',
              icon: '$sensor',
              validate: (user) => {
                return user.attributes.permission_sensor;
              },
            },
            component: () => import('@/views/Sensor.vue'),
          },
          {
            path: '/sensor-categories',
            name: 'sensor-categories',
            meta: {
              title: 'sensor-categories',
              group: 'apps',
              icon: 'mdi-tag-multiple',
              validate: (user) => !!user.attributes.permission_sensor,
            },
            component: () => import('@/views/SensorCategories.vue'),
          },
        ],
      },
      // ─── Medidas cautelares ───
      // Grupo propio: es un módulo independiente, no una sección de
      // dispositivos. Las 4 pantallas exponen datos personales sensibles
      // (CURP, domicilio, orden judicial), así que el permiso se valida en el
      // grupo Y en cada hija — copiar el patrón CRUD sin meta.validate
      // dejaría la PII accesible a cualquier usuario autenticado.
      {
        path: '/medidas-cautelares',
        name: 'medidas-cautelares',
        component: LayoutChildren,
        redirect: '/mc-personas',
        meta: {
          title: 'medidas-cautelares',
          group: 'apps',
          icon: 'mdi-account-lock',
          validate: (user) => user.attributes.permission_medidas_cautelares,
        },
        children: [
          {
            path: '/mc-personas',
            name: 'mc-personas',
            meta: {
              title: 'mc-personas',
              group: 'apps',
              icon: 'mdi-account-lock',
              validate: (user) => user.attributes.permission_medidas_cautelares,
            },
            component: () => import('@/views/McPersonas.vue'),
          },
          {
            path: '/mc-ordenes',
            name: 'mc-ordenes',
            meta: {
              title: 'mc-ordenes',
              group: 'apps',
              icon: 'mdi-gavel',
              validate: (user) => user.attributes.permission_medidas_cautelares,
            },
            component: () => import('@/views/McOrdenes.vue'),
          },
          {
            path: '/mc-dispositivos',
            name: 'mc-dispositivos',
            meta: {
              title: 'mc-dispositivos',
              group: 'apps',
              icon: 'mdi-watch-variant',
              validate: (user) => user.attributes.permission_medidas_cautelares,
            },
            component: () => import('@/views/McDispositivos.vue'),
          },
          {
            path: '/mc-centros',
            name: 'mc-centros',
            meta: {
              title: 'mc-centros',
              group: 'apps',
              icon: 'mdi-shield-home',
              validate: (user) => user.attributes.permission_medidas_cautelares,
            },
            component: () => import('@/views/McCentros.vue'),
          },
          {
            // Ruta histórica del seguimiento individual: ahora vive en la
            // consola de pantalla completa. Se conserva como redirect para
            // no romper enlaces guardados.
            path: '/medidas-cautelares/seguimiento/:personaId',
            name: 'mc-seguimiento',
            meta: { title: 'mc-monitor', group: 'apps', hiddenInMenu: true },
            redirect: (to) => ({
              name: 'mc-monitor-persona',
              params: { personaId: to.params.personaId },
            }),
          },
        ],
      },
      {
        path: '/dispositivos',
        name: 'dispositivos',
        component: LayoutChildren,
        redirect: '/devices',
        meta: {
          title: 'dispositivos',
          group: 'apps',
          icon: 'mdi-car-multiple',
        },
        children: [
          {
            path: '/devices',
            name: 'devices',
            meta: {
              title: 'devices',
              group: 'apps',
              icon: '$device',
              validate: (user) => {
                return user.attributes.permission_devices;
              },
            },
            component: () => import('@/views/Devices.vue'),
          },
          {
            path: '/deviceCategories',
            name: 'deviceCategories',
            meta: {
              title: 'deviceCategories',
              group: 'apps',
              icon: 'mdi-tag-multiple',
              validate: (user) => !!user.attributes.permission_devices,
            },
            component: () => import('../views/DeviceCategories.vue'),
          },
          {
            path: '/drivers',
            name: 'drivers',
            meta: {
              title: 'drivers',
              group: 'apps',
              icon: '$driver',
              validate: (user) => !!user.attributes.permission_devices,
            },
            component: () => import('@/views/Drivers.vue'),
          },
          {
            path: '/fuelPrice',
            name: 'fuelPrice',
            meta: {
              title: 'fuelPrice',
              group: 'apps',
              icon: 'mdi-fuel',
              validate: (user) => !!user.attributes.permission_devices,
            },
            component: () => import('@/views/FuelPrice.vue'),
          },
        ],
      },
      // {
      //   path: '/drivers',
      //   name: 'drivers',
      //   meta: {
      //     title: 'drivers',
      //     group: 'apps',
      //     icon: '$driver',
      //   },
      //   component: () => import('@/views/Drivers.vue'),
      // },
      // {
      //   path: '/maintenance',
      //   name: 'maintenance',
      //   meta: {
      //     title: 'maintenance',
      //     group: 'apps',
      //     icon: 'mdi-hammer-wrench',
      //   },
      //   component: () => import('@/views/Maintenance.vue'),
      // },
      {
        path: '/routes',
        name: 'routes',
        meta: {
          title: 'routes',
          group: 'apps',
          icon: 'mdi-map-marker-path',
          validate: (user) => !!user.attributes.permission_devices,
        },
        component: () => import('@/views/Routes.vue'),
      },
      {
        path: '/incident-categories',
        name: 'incident-categories',
        meta: {
          title: 'incident-categories',
          group: 'apps',
          icon: 'mdi-format-list-bulleted-type',
          admin: true,
        },
        component: () => import('@/views/IncidentCategories.vue'),
      },

    ],
  },
  {
    path: '/reports',
    component: LayoutReports,
    meta: {
      title: 'home',
      group: 'apps',
      icon: '',
    },
    // Antes redirigía a '/home', que es admin-only: un no-administrador que
    // entrara a /reports por un marcador recibía denegación, toast y rebote.
    // El launcher nunca manda aquí —usa sectionEntry, que ya resuelve el
    // primer reporte accesible— así que solo afectaba a la vía del marcador.
    // Mismo patrón que /administracion.
    redirect: () => primerHijoAccesible('/reports'),
    children: [
      {
        path: '/reports/users',
        name: 'users-reports',
        meta: {
          title: 'users',
          group: 'apps',
          icon: 'mdi-account-group-outline',
          admin: true,
        },
        component: () => import('@/views/reports/UsersReports.vue'),
      },
      {
        path: '/reports/accesos',
        name: 'accesos-reports',
        meta: {
          title: 'accesos',
          group: 'apps',
          icon: 'mdi-shield-account-outline',
          admin: true,
        },
        component: () => import('@/views/reports/AccessReport.vue'),
      },
      {
        path: '/reports/cameras',
        name: 'cameras-reports',
        meta: {
          // Clave propia, NO 'cameras': ese título lo comparte el menú del CRUD
          // de cámaras (sección admin, arriba en este mismo archivo). Reusarlo
          // aquí habría renombrado también esa pantalla a "Cámaras (anterior)",
          // que no es vieja ni va a retirarse — es el padrón vivo. El reporte
          // viejo de unidades hacía lo mismo antes de retirarse.
          title: 'cameras-reports',
          group: 'apps',
          icon: '$camera-white',
          validate: (user) => !!user.attributes.permission_cameras,
        },
        component: () => import('@/views/reports/CamerasReports.vue'),
      },
      {
        path: '/reports/camaras',
        name: 'camaras-reports',
        meta: {
          title: 'camaras',
          group: 'apps',
          icon: 'mdi-cctv',
          // Reusa el permiso del reporte que sustituye: conviviendo, quien ve
          // el viejo ve el nuevo desde el primer día sin que un administrador
          // tenga que repartir nada. Mismo criterio que /reports/unidades con
          // permission_devices. Por eso NO hay casilla nueva que agregar y
          // permission-grantability.test.js sigue en verde.
          validate: (user) => !!user.attributes.permission_cameras,
        },
        component: () => import('@/views/reports/CameraReports.vue'),
      },
      // {
      //   path: '/reports/bus',
      //   name: 'bus-reports',
      //   meta: {
      //     title: 'bus',
      //     group: 'apps',
      //     icon: '$bus-white',
      //   },
      //   component: () => import('@/views/reports/BusesReports.vue'),
      // },
      // {
      //   path: '/reports/lpr',
      //   name: 'lpr-reports',
      //   meta: {
      //     title: 'lpr',
      //     group: 'apps',
      //     icon: '$lpr-white',
      //   },
      //   component: () => import('@/views/reports/LprsReports.vue'),
      // },
      // RETIRADO 2026-10-14 — «Unidades (anterior)» (/reports/devices).
      // Sus seis sub-reportes salieron de uno en uno, cada uno medido contra
      // este backend: «Rutas» el 2026-09-03 (lo sustituye historial-unidades),
      // Resumen, Gasolina, Alertamientos y Presencia en área el 2026-09-09
      // (404, columnas en cero, o cubiertos por Actividad y Bitácora), y
      // «Eventos» al último, porque era el único que pintaba los eventos en un
      // mapa. Ese hueco lo cerró el mapa de la Bitácora (`UnitEventMap.vue`,
      // 2026-09-25), así que la ruta ya no tenía nada que /reports/unidades no
      // diera. Esta vez sí se borraron la vista y components/devicesReports/:
      // el «quitar la opción y dejar el archivo» de los retiros anteriores era
      // para esperar este cutover. Queda solo `devicesReports/data.js`, que
      // importa Map.vue (legacy). La URL vieja redirige: ver publicRoute.
      {
        path: '/reports/historial-unidades',
        name: 'historial-unidades-reports',
        meta: {
          title: 'historial-unidades',
          group: 'apps',
          icon: 'mdi-map-marker-path',
          validate: (user) => !!user.attributes.permission_device_history,
        },
        component: () => import('@/views/reports/RouteHistory.vue'),
      },
      {
        path: '/reports/unidades',
        name: 'unidades-reports',
        meta: {
          title: 'unidades',
          group: 'apps',
          icon: 'mdi-gauge',
          // Reusa el permiso del reporte que sustituye: conviviendo, quien ve
          // el viejo ve el nuevo desde el primer día sin que un administrador
          // tenga que repartir nada.
          validate: (user) => !!user.attributes.permission_devices,
        },
        component: () => import('@/views/reports/UnitReports.vue'),
      },
      {
        path: '/reports/incidents',
        name: 'incidents-reports',
        meta: {
          title: 'incidents-reports',
          group: 'apps',
          icon: 'mdi-alert-outline',
          validate: (user) => !!user.attributes.permission_incidents,
        },
        component: () => import('@/views/reports/IncidentsReports.vue'),
      },
      {
        path: '/reports/incidentes',
        name: 'incidentes-reports',
        meta: {
          title: 'incidentes',
          group: 'apps',
          icon: 'mdi-alert-outline',
          // Reusa el permiso del reporte que sustituye, como /reports/camaras
          // con permission_cameras: conviviendo, quien ve el viejo ve el nuevo
          // sin que un administrador reparta nada. Sin casilla nueva.
          validate: (user) => !!user.attributes.permission_incidents,
        },
        component: () => import('@/views/reports/IncidentReports.vue'),
      },
      {
        path: '/reports/botones',
        name: 'botones-reports',
        meta: {
          title: 'botones',
          group: 'apps',
          icon: '$sensor',
          // El mismo permiso que el listado de botones (/sensor): quien
          // administra los botones ve su reporte. Singular, como siempre lo
          // exigió esta tabla de rutas (ver users.model.js).
          validate: (user) => !!user.attributes.permission_sensor,
        },
        component: () => import('@/views/reports/PanicButtonReports.vue'),
      },
      // RETIRADO 2026-09-25 — «Alertamiento Colibrí» (/reports/sos).
      // Medido contra este backend: TODO `kalan-sos` devuelve 404
      // `NotFoundException` (cuerpo de NestJS, o sea que la petición llega a
      // apiv2-edomex y apiv2 no tiene la ruta). Tampoco responde bajo otro
      // prefijo: `/v2/kalan-sos/…`, `/kalan-sos`, `/sos-calls` y
      // `/emergencyCalls`, los cuatro 404. 47 de 731 usuarios llevan
      // `permission_sinaloaSOS`, así que 47 personas veían la entrada de menú
      // para abrir una pantalla que solo podía contestar «Ocurrió un error al
      // cargar los datos» al pulsar Filtrar.
      //
      // Se retira quitando la entrada, no borrando archivos: `SosReports.vue` y
      // `components/sosReports/` se quedan en el árbol sin importador hasta el
      // cutover, igual que «Rutas» y los cuatro sub-reportes de unidades.
      //
      // NO es el caso de «Presencia en área», que nunca existió. Las llamadas
      // SOS sí ocurren en vivo (socket `/ws/appclient`, `sos-call-service`, el
      // panel SOS de MapV2); lo que no existe es el almacén histórico —
      // `/sosincidents` contesta 200 vacío con todos sus filtros y
      // `/reports/sosincidents` es 404—, así que hoy no hay a dónde migrarlo.
      // Si alguien decide persistir las llamadas en apiv2, la pantalla se
      // reconstruye con el stack de Map V2 como los otros cinco pilotos.
      //
      // `permission_sinaloaSOS` NO se toca: gatea además el socket SOS
      // (`util/mapSockets.js`), el SOS de Map V2 (`socketio-handler.js`) y la
      // pestaña SOS del mapa legacy. Sigue siendo un permiso vivo y otorgable.
    ],
  },
  {
    // Mapa legacy (OpenLayers CDN). Congelado: solo accesible por URL
    // directa (no aparece en menú ni en navegación programática). Se
    // mantiene como fallback de emergencia mientras se valida V2 en
    // producción; se eliminará en una fase posterior junto con
    // src/views/Map.vue y src/components/map/.
    path: '/map-legacy',
    name: 'map-legacy',
    component: () => import('@/views/Map.vue'),
    meta: {
      title: 'map-legacy',
      group: 'map',
      icon: 'mdi-maps',
      hiddenInMenu: true,
      admin: true,
    },
  },
  {
    // Mapa canónico (MapLibre + entity-configs). Antes vivía en /map-v2.
    path: '/map',
    name: 'map',
    component: () => import('@/views/MapV2.vue'),
    meta: { title: 'map', group: 'map', icon: 'mdi-map' },
  },
  {
    // Retro-compat: bookmarks y enlaces guardados al path anterior siguen
    // funcionando redirigiendo al nuevo destino canónico.
    path: '/map-v2',
    redirect: '/map',
  },
  {
    // Retro-compat: el antiguo pseudo-ítem de menú. Se conserva como redirect
    // para no romper marcadores. Sin `name` a propósito: la ruta /map ya usa
    // `name: 'map'` y declararlo aquí lo duplicaría.
    path: '/maps',
    redirect: '/map',
  },
];

export const protectedRoutesWithoutLayout = [
  {
    path: '/analytics/monitor',
    name: 'analytics-monitor',
    meta: {
      title: 'analytics-monitor',
      group: 'apps',
      icon: '$lpr',
      validate: (user) => !!user.attributes.permission_analytics_monitor,
    },
    component: () => import('@/views/AnalyticsMonitor.vue'),
  },
  // Consola de monitoreo de medidas cautelares: pantalla completa sin el
  // layout de admin, como el monitor de LPR. Listados | mapa | expediente.
  {
    path: '/medidas-cautelares/monitor',
    name: 'mc-monitor',
    meta: {
      title: 'mc-monitor',
      group: 'apps',
      icon: 'mdi-account-lock',
      validate: (user) => user.attributes.permission_medidas_cautelares,
    },
    component: () => import('@/views/McMonitor.vue'),
  },
  {
    // Misma consola con una persona preseleccionada — es la URL a la que
    // llevan el mapa principal y el padrón.
    path: '/medidas-cautelares/monitor/:personaId',
    name: 'mc-monitor-persona',
    meta: {
      title: 'mc-monitor',
      group: 'apps',
      icon: 'mdi-account-lock',
      hiddenInMenu: true,
      validate: (user) => user.attributes.permission_medidas_cautelares,
    },
    component: () => import('@/views/McMonitor.vue'),
  },
  {
    // Banco de pruebas: emite posiciones y eventos de brazalete a mano para
    // ver la reacción del sistema sin hardware. La vista se apaga sola si el
    // backend responde que MC_SIMULADOR no está activo.
    path: '/medidas-cautelares/simulador',
    name: 'mc-simulador',
    meta: {
      title: 'mc-simulador',
      group: 'apps',
      icon: 'mdi-flask-outline',
      // Fuera del menú: en un sistema que sostiene medidas cautelares
      // judiciales, un "simulador" a la vista se presta a malas lecturas. Se
      // llega por URL, o por el botón que la consola de monitoreo muestra
      // solo cuando el backend reporta el simulador encendido.
      hiddenInMenu: true,
      validate: (user) => user.attributes.permission_medidas_cautelares,
    },
    component: () => import('@/views/McSimulador.vue'),
  },
  {
    path: '/analytics/history',
    name: 'analytics-history',
    meta: {
      title: 'analytics-history',
      group: 'apps',
      icon: '$lpr',
      validate: (user) => !!user.attributes.permission_analytics_monitor,
    },
    component: () => import('@/views/AnalyticsHistory.vue'),
  },
  {
    path: '/portal/incidentes',
    name: 'incident-portal',
    meta: {
      title: 'incident-portal',
      group: 'apps',
      icon: 'mdi-shield-alert-outline',
      validate: (user) => {
        // Acceso a la vista ≠ encierro: el helper compartido cubre el flag
        // (quien está encerrado siempre entra); las corporaciones dan acceso
        // adicional a usuarios C5 sin encierro — sus acciones dentro del
        // portal siguen gateadas por sus propios cad_permission_*.
        if (isPortalIncidentsUser(user)) return true
        const attrs = (user && user.attributes) || {}
        const corps = attrs.corporaciones
        if (Array.isArray(corps)) return corps.length > 0
        if (typeof corps === 'string') return corps.trim().length > 0
        return false
      },
    },
    component: () => import('@/views/IncidentPortal.vue'),
  },
];
